import { type IconName, itemIcon } from './icons.ts';
import type { PathEntry } from './schemas/common.ts';
import type { Item } from './schemas/item.ts';
import type { PlanPinNodeType } from './schemas/plan.ts';
import type { ClientTree, PropertyTree, RoomTree } from './schemas/responses.ts';

// The one walk over a client tree. Summaries, generated names, connection
// rules, and the pickers all read the same nested structure; they call
// `walkRooms` (or the collectors below) instead of each recursing on their
// own, so "everything under a room" means one thing everywhere.

export interface RoomContext {
  readonly propertyId: string;
  readonly parentId: string | null;
  readonly depth: number;
  /** Names from the top-level room down to this one. */
  readonly path: readonly string[];
}

export type RoomVisitor = (room: RoomTree, context: RoomContext) => void;

function walk(
  rooms: readonly RoomTree[],
  propertyId: string,
  parentId: string | null,
  depth: number,
  path: readonly string[],
  visit: RoomVisitor,
): void {
  for (const room of rooms) {
    const here = [...path, room.room.name];
    visit(room, { propertyId, parentId, depth, path: here });
    walk(room.rooms, propertyId, room.room.id, depth + 1, here, visit);
  }
}

/** Visit every room of these rooms, depth first, sub-rooms after their parent. */
export function walkRooms(
  rooms: readonly RoomTree[],
  propertyId: string,
  visit: RoomVisitor,
): void {
  walk(rooms, propertyId, null, 0, [], visit);
}

/** Visit every room of every property. */
export function walkProperties(properties: readonly PropertyTree[], visit: RoomVisitor): void {
  for (const property of properties) {
    walkRooms(property.rooms, property.property.id, visit);
  }
}

export interface PlacedItem {
  readonly item: Item;
  readonly propertyId: string;
  readonly roomId: string;
  readonly rackId: string | null;
}

/** Every item in these rooms with where it sits, room items before rack items. */
export function itemsIn(rooms: readonly RoomTree[], propertyId = ''): PlacedItem[] {
  const out: PlacedItem[] = [];
  walkRooms(rooms, propertyId, (room, context) => {
    for (const item of room.items) {
      out.push({ item, propertyId: context.propertyId, roomId: room.room.id, rackId: null });
    }
    for (const rack of room.racks) {
      for (const item of rack.items) {
        out.push({
          item,
          propertyId: context.propertyId,
          roomId: room.room.id,
          rackId: rack.rack.id,
        });
      }
    }
  });
  return out;
}

/** Something a floor-plan pin can point at, with the icon its marker wears. */
export interface PinTarget {
  readonly type: PlanPinNodeType;
  readonly id: string;
  readonly name: string;
  readonly icon: IconName;
}

/**
 * Everything under these rooms a pin can point at: every device (in a room
 * or in a rack), every rack, and every cable drop, in the order the tree
 * reads. Rooms themselves are what a plan is of, so they are not targets.
 */
export function pinTargetsIn(rooms: readonly RoomTree[]): PinTarget[] {
  const out: PinTarget[] = [];
  walkRooms(rooms, '', (room) => {
    for (const item of room.items) {
      out.push({ type: 'item', id: item.id, name: item.name, icon: itemIcon(item.type) });
    }
    for (const rack of room.racks) {
      out.push({ type: 'rack', id: rack.rack.id, name: rack.rack.name, icon: 'rack' });
      for (const item of rack.items) {
        out.push({ type: 'item', id: item.id, name: item.name, icon: itemIcon(item.type) });
      }
    }
    for (const drop of room.drops) {
      out.push({ type: 'drop', id: drop.id, name: drop.label, icon: 'cable' });
    }
  });
  return out;
}

/** A room a plan shape can be linked to: its id and its name down the tree. */
export interface RoomChoice {
  readonly id: string;
  readonly name: string;
}

/**
 * Every room under these rooms, each named by its path from the top room down,
 * so a floor-plan room outline can be linked to the room it draws even when
 * two rooms share a short name.
 */
export function roomChoicesIn(rooms: readonly RoomTree[]): RoomChoice[] {
  const out: RoomChoice[] = [];
  walkRooms(rooms, '', (room, context) => {
    out.push({ id: room.room.id, name: context.path.join(' › ') });
  });
  return out;
}

/**
 * Every item of one client with the path from the client down to it, from
 * a single tree instead of a query per item (the bulk form of
 * `pathForItem` in worker/db/paths.ts, for a reply that names many items).
 */
export function itemPaths(tree: {
  readonly client: Pick<ClientTree['client'], 'id' | 'name' | 'identifier'>;
  readonly properties: ClientTree['properties'];
}): Map<string, PathEntry[]> {
  const paths = new Map<string, PathEntry[]>();
  const client: PathEntry = {
    type: 'client',
    id: tree.client.id,
    name: tree.client.name,
    identifier: tree.client.identifier,
  };
  for (const { property, rooms } of tree.properties) {
    const above: PathEntry[] = [
      client,
      { type: 'property', id: property.id, name: property.name, identifier: property.identifier },
    ];
    const roomPaths = new Map<string, PathEntry[]>();
    // Parents are visited before their sub-rooms, so the chain is always ready.
    walkRooms(rooms, property.id, (subtree, context) => {
      const room = subtree.room;
      const here = [
        ...(context.parentId === null ? above : (roomPaths.get(context.parentId) ?? above)),
        { type: 'room' as const, id: room.id, name: room.name, identifier: room.identifier },
      ];
      roomPaths.set(room.id, here);
      for (const item of subtree.items) {
        paths.set(item.id, [
          ...here,
          { type: 'item', id: item.id, name: item.name, identifier: item.identifier },
        ]);
      }
      for (const rack of subtree.racks) {
        const inRack = [
          ...here,
          {
            type: 'rack' as const,
            id: rack.rack.id,
            name: rack.rack.name,
            identifier: rack.rack.identifier,
          },
        ];
        for (const item of rack.items) {
          paths.set(item.id, [
            ...inRack,
            { type: 'item', id: item.id, name: item.name, identifier: item.identifier },
          ]);
        }
      }
    });
  }
  const items = new Map(
    tree.properties.flatMap(({ property, rooms }) =>
      itemsIn(rooms, property.id).map(({ item }): [string, Item] => [item.id, item]),
    ),
  );
  return nestItemPaths(paths, items);
}

/** Resolve device ancestry over known container paths; cached ancestor paths may be anchors. */
export function nestItemPaths(
  paths: Map<string, PathEntry[]>,
  items: ReadonlyMap<string, Item>,
): Map<string, PathEntry[]> {
  const resolved = new Set<string>();
  const resolve = (id: string, seen: Set<string>): PathEntry[] => {
    const current = paths.get(id);
    const item = items.get(id);
    if (current === undefined) {
      throw new Error('The device ancestor is missing from the tree');
    }
    if (item === undefined || resolved.has(id)) {
      return current;
    }
    if (seen.has(id)) {
      throw new Error('The device ancestry contains a cycle');
    }
    seen.add(id);
    const parent = item.portParentItemId ?? item.parentItemId;
    if (parent !== null) {
      const above = resolve(parent, seen).map((entry, index, chain) =>
        index === chain.length - 1 && item.portParentPort !== null
          ? { ...entry, port: item.portParentPort }
          : entry,
      );
      const nested: PathEntry[] = [
        ...above,
        { type: 'item', id, name: item.name, identifier: item.identifier },
      ];
      paths.set(id, nested);
    }
    resolved.add(id);
    return paths.get(id) ?? current;
  };
  for (const id of paths.keys()) {
    resolve(id, new Set());
  }

  return paths;
}

/** The rack with this id and its items, wherever the rack's room nests. */
export function findRackTree(
  rooms: readonly RoomTree[],
  rackId: string,
): RoomTree['racks'][number] | undefined {
  let found: RoomTree['racks'][number] | undefined;
  walkRooms(rooms, '', (room) => {
    found ??= room.racks.find((rack) => rack.rack.id === rackId);
  });
  return found;
}

/** The room subtree with this id, wherever it nests. */
export function findRoomTree(rooms: readonly RoomTree[], roomId: string): RoomTree | undefined {
  let found: RoomTree | undefined;
  walkRooms(rooms, '', (room) => {
    if (found === undefined && room.room.id === roomId) {
      found = room;
    }
  });
  return found;
}
